import { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../api/api';
import {
  Box,
  IconButton,
  CircularProgress,
  Paper,
  Typography,
  TextField,
  Avatar,
  Fade,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

const BUTTON_SIZE = 56;
const POPUP_WIDTH = 380;
const POPUP_HEIGHT = 500;
const GAP = 12;

function getPopupPosition(btnX, btnY) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const btnCenterX = btnX + BUTTON_SIZE / 2;
  const spaceAbove = btnY;
  const spaceBelow = vh - btnY - BUTTON_SIZE;
  const openAbove = spaceAbove >= POPUP_HEIGHT + GAP || spaceAbove > spaceBelow;

  let top;
  if (openAbove) {
    top = Math.max(8, btnY - POPUP_HEIGHT - GAP);
  } else {
    top = btnY + BUTTON_SIZE + GAP;
  }
  const popupH = openAbove
    ? Math.min(POPUP_HEIGHT, spaceAbove - GAP - 8)
    : Math.min(POPUP_HEIGHT, spaceBelow - GAP - 8);

  let left = btnCenterX - POPUP_WIDTH / 2;
  left = Math.max(8, Math.min(left, vw - POPUP_WIDTH - 8));

  return { top, left, height: Math.max(250, popupH) };
}

function AiSupportChat() {
  const userDetails = useSelector((state) => state.user.userDetails);
  const isAdmin = userDetails?.role === 1;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Hello! I\'m your AI Assistant.\nHow can I help you today? You can search anything across the system.',
    },
  ]);
  const [input, setInput] = useState('');
  const [position, setPosition] = useState({ x: null, y: null });
  const dragRef = useRef(null);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);
  const DRAG_THRESHOLD = 5;

  useEffect(() => {
    if (position.x === null) {
      setPosition({
        x: window.innerWidth - BUTTON_SIZE - 24,
        y: window.innerHeight - BUTTON_SIZE - 24,
      });
    }
  }, [position.x]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const clamp = useCallback((x, y) => {
    return {
      x: Math.max(0, Math.min(x, window.innerWidth - BUTTON_SIZE)),
      y: Math.max(0, Math.min(y, window.innerHeight - BUTTON_SIZE)),
    };
  }, []);

  const onPointerDown = useCallback((e) => {
    isDragging.current = true;
    startPos.current = { x: e.clientX, y: e.clientY };
    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    dragRef.current.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      const newPos = clamp(
        e.clientX - dragOffset.current.x,
        e.clientY - dragOffset.current.y
      );
      setPosition(newPos);
    },
    [clamp, DRAG_THRESHOLD]
  );

  const onPointerUp = useCallback((e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
      setOpen((prev) => !prev);
    }
  }, [DRAG_THRESHOLD]);

  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/ai-support/chat', { question: trimmed });
      const answer = res?.answer || 'No response received.';
      const sources = res?.sources || [];
      let sourceText = '';
      if (sources.length > 0) {
        sourceText = '\n\nSources: ' + sources.map((s) => `${s.table} (${s.count})`).join(', ');
      }
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: answer + sourceText },
      ]);
    } catch (err) {
      console.error('AI Support error:', err);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: 'Sorry, something went wrong. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const popupPos = getPopupPosition(position.x, position.y);

  if (!isAdmin) return null;
  if (position.x === null) return null;

  return (
    <>
      {/* Floating button */}
      <Box
        ref={dragRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        sx={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: 9999,
          width: BUTTON_SIZE,
          height: BUTTON_SIZE,
          borderRadius: '50%',
          background: '#701313',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isDragging.current ? 'grabbing' : 'grab',
          boxShadow: '0 4px 20px rgba(112,19,19,0.5)',
          touchAction: 'none',
          userSelect: 'none',
          transition: isDragging.current ? 'none' : 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: '0 6px 28px rgba(112,19,19,0.7)',
          },
        }}
      >
        <SupportAgentIcon sx={{ color: '#fff', fontSize: 30 }} />
      </Box>

      {/* Chat popup */}
      <Fade in={open}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            top: popupPos.top,
            left: popupPos.left,
            width: POPUP_WIDTH,
            maxWidth: 'calc(100vw - 16px)',
            height: popupPos.height,
            zIndex: 9998,
            borderRadius: 3,
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: '#701313',
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupportAgentIcon sx={{ color: '#fff', fontSize: 24 }} />
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>
                AI Support
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon sx={{ color: '#fff', fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: '#f7f8fc',
            }}
          >
            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  gap: 1,
                  alignItems: 'flex-end',
                }}
              >
                {msg.role === 'assistant' && (
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      background: '#701313',
                      fontSize: 14,
                    }}
                  >
                    <SupportAgentIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '75%',
                    px: 1.5,
                    py: 1,
                    borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    bgcolor: msg.role === 'user' ? '#701313' : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#333',
                    fontSize: 14,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    lineHeight: 1.5,
                  }}
                >
                  {msg.text.split('\n').map((line, j) => (
                    <span key={j}>
                      {j > 0 && <br />}
                      {j === 0 && msg.role === 'assistant' && msg === messages[0] ? (
                        <><strong>{line}</strong></>
                      ) : line}
                    </span>
                  ))}
                </Box>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    background: '#701313',
                    fontSize: 14,
                  }}
                >
                  <SupportAgentIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <CircularProgress size={20} sx={{ color: '#701313' }} />
                <Typography sx={{ fontSize: 13, color: '#888' }}>Searching...</Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid #eee',
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              bgcolor: '#fff',
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search anything..."
              value={input}
              disabled={loading}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: 14,
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={loading}
              sx={{
                background: loading ? '#ccc' : '#701313',
                color: '#fff',
                '&:hover': { background: loading ? '#ccc' : '#8a1a1a' },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Box>
        </Paper>
      </Fade>
    </>
  );
}

export default AiSupportChat;
